import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "jl88_cart";

function loadInitialCart() {
  // Synchronous hydration — must happen on the very first render so the
  // save-effect doesn't fire with an empty array and wipe localStorage.
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadInitialCart);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch { /* ignore quota / private-mode errors */ }
  }, [items]);

  function add(product, variant) {
    const key = `${product.id}:${variant?.id ?? "default"}`;
    setItems((prev) => {
      const exist = prev.find((i) => i.key === key);
      if (exist) return prev.map((i) => (i.key === key ? { ...i, qty: i.qty + 1 } : i));
      return [
        ...prev,
        {
          key,
          product_id: product.id,
          title: product.title,
          image: product.default_image,
          variant_id: variant?.id,
          variant_title: variant?.title,
          price: variant?.price ?? product.min_price,
          checkout_url: product.checkout_url,
          qty: 1,
        },
      ];
    });
    setOpen(true);
  }

  const remove = (key) => setItems((prev) => prev.filter((i) => i.key !== key));
  const setQty = (key, qty) =>
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, qty: Math.max(1, qty) } : i)));
  const clear = () => setItems([]);

  const totals = useMemo(() => {
    const count = items.reduce((s, i) => s + i.qty, 0);
    const subtotal = items.reduce((s, i) => s + (i.price || 0) * i.qty, 0);
    return { count, subtotal };
  }, [items]);

  return (
    <CartContext.Provider value={{ items, add, remove, setQty, clear, open, setOpen, totals }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
